import { useEffect, useState } from "react";
import PolicyDocumentLayout from "./PolicyDocumentLayout";
import MarkdownPolicyDocument from "./MarkdownPolicyDocument";
import { apiGetPolicies, apiGetPolicyDocument } from "../../services/policyService";
import { parseHeadingsToToc } from "../../utils/policyMarkdown";

// Generic loading/error/ready orchestration for any versioned policy
// document — Rules today (RulesPage.js is a one-line wrapper around this),
// Terms/Privacy later with zero duplicated fetch/TOC/render logic, just a
// different `documentType`.
//
// Never falls back to stale/hardcoded content on failure — a policy that
// can't be confirmed loaded is not something a user should be treated as
// having acknowledged or been shown.
export default function PolicyPage({ documentType, title, subtitle, backTo }) {
  const [state, setState] = useState({ status: "loading", doc: null, title: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const policies = await apiGetPolicies();
        const meta = policies?.[documentType];
        if (!meta) throw new Error(`No registered policy metadata for "${documentType}"`);
        const doc = await apiGetPolicyDocument(documentType, meta.version);
        if (!cancelled) setState({ status: "ready", doc, title: meta.title });
      } catch {
        if (!cancelled) setState({ status: "error", doc: null, title: null });
      }
    })();
    return () => { cancelled = true; };
  }, [documentType]);

  const resolvedTitle = title ?? state.title ?? "";
  const toc = state.status === "ready" ? parseHeadingsToToc(state.doc.contentMarkdown) : [];

  return (
    <PolicyDocumentLayout
      title={resolvedTitle}
      subtitle={subtitle}
      lastUpdated={state.status === "ready" ? state.doc.lastUpdated : null}
      backTo={backTo}
      toc={toc}
      contentReady={state.status === "ready"}
    >
      {state.status === "loading" && <p>Loading…</p>}
      {state.status === "error" && <p>Unable to load this document right now. Please try again.</p>}
      {state.status === "ready" && <MarkdownPolicyDocument markdown={state.doc.contentMarkdown} />}
    </PolicyDocumentLayout>
  );
}
